import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import PropTypes from 'prop-types';
import Icon from '@/components/atoms/icon';
import Input from '@/components/atoms/input';
import { useClickOutside } from '@/hooks/useClickOutside';

const SearchBox = ({
	type,
	placeholder,
	query,
	onClick,
	onChange,
	handleClearSearch,
	selectedValue,
	results = [],
	disabled,
	className,
	inputClassName,
	searchIconClassName,
	closeIconClassName,
}) => {
	const inputRef = useRef(null);
	const containerRef = useRef(null);
	const [localSelectedValue, setLocalSelectedValue] = useState(false);

	let name = '';
	let iconName = '';
	switch (type.toLowerCase()) {
		case 'client':
			name = 'Clients';
			iconName = 'clients';
			break;
		case 'brokers':
			name = 'Brokers';
			iconName = 'third-parties';
			break;
		case 'policies':
			name = 'Policies';
			iconName = 'policies';
			break;
		case 'introducers':
			name = 'Introducers';
			iconName = 'third-parties';
			break;
		default:
			break;
	}

	useEffect(() => {
		setLocalSelectedValue(selectedValue);
	}, [selectedValue]);

	const handleInputClick = () => {
		// Set localSelectedValue to null
		setLocalSelectedValue(null);
	};

	const handleOnChange = (e) => {
		// Call the original onChange function
		onChange(e);
	};

	useClickOutside(containerRef, () => {
		setLocalSelectedValue(true);
	});

	return (
		<div ref={containerRef} className={cn('relative block overflow-visible bg-transparent rounded-none', className)}>
			<Input
				ref={inputRef}
				type="search"
				placeholder={placeholder}
				aria-label="Search"
				value={selectedValue || query}
				disabled={disabled}
				className={inputClassName}
				searchIconClassName={searchIconClassName}
				closeIconClassName={closeIconClassName}
				onChange={handleOnChange}
				onClick={handleInputClick}
				handleClearSearch={() => {
					setLocalSelectedValue(null);
					handleClearSearch();
				}}
			/>
			{results.length > 0 && !localSelectedValue && (
				<div className="absolute left-0 right-0 mt-1 bg-white border rounded-md top-full shadow-drop-shadow border-formOutline max-h-[300px] overflow-y-auto overflow-x-hidden z-50">
					<ul>
						<li className="mb-1 last:mb-0">
							{name !== '' && <p className="px-2 mx-2 py-2.5 text-sm font-bold leading-eis-18 text-eis-black">{name}</p>}
							{results.map((result) => (
								<a
									onClick={(e) => {
										onClick(e, result);
										inputRef?.current?.setShowSearchInputCloseBtn(false);
									}}
									className="flex flex-row gap-2 p-2 mx-1 mt-1 select-none"
								>
									<Icon name={iconName} className="text-eis-grey-87 shrink-0" />
									<p className="flex flex-col">
										<span className="block mb-2 text-sm text-eis-black leading-eis-18 last:mb-0">
											{result.name} {type.toLowerCase() === 'clients' ? `(${result.id})` : ''}
										</span>
									</p>
								</a>
							))}
						</li>
					</ul>
				</div>
			)}
			{query && results.length == 0 && localSelectedValue == null && (
				<div className="absolute left-0 right-0 mt-1 bg-white border rounded-md top-full shadow-drop-shadow border-formOutline max-h-[300px] overflow-y-auto overflow-x-hidden z-50">
					<p className="py-6 text-sm font-normal text-center leading-eis-18 text-eis-black">No results found.</p>
				</div>
			)}
		</div>
	);
};

SearchBox.propTypes = {
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func,
};

SearchBox.defaultProps = {
	value: '',
	onChange: () => {},
};

export default SearchBox;
